import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';

/**
 * Custom hook for managing rooms data and operations
 *
 * UPDATED: Now uses apiService instead of direct axios calls
 * - Removed redundant auth headers (apiService handles this automatically)
 * - Added request cancellation for fetchRooms
 * - Cleaner error handling
 */
export const useRooms = (API) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch rooms from API
   * Now includes request cancellation
   */
  const fetchRooms = useCallback(async () => {
    // Create abort controller for request cancellation
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get('/admin/rooms', {
        signal: controller.signal
      });

      // Sort rooms by room_number in ascending order
      const sortedRooms = response.data.sort((a, b) => {
        const numA = parseInt(a.room_number) || 0;
        const numB = parseInt(b.room_number) || 0;
        return numA - numB;
      });

      setRooms(sortedRooms);
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }

      setError(err);
      toast.error('Failed to fetch rooms');
    } finally {
      setLoading(false);
    }

    // Return cleanup function
    return () => controller.abort();
  }, []);

  /**
   * Create new room
   */
  const createRoom = useCallback(async (roomData) => {
    try {
      await apiService.post('/admin/rooms', roomData);
      toast.success('Room created successfully');
      await fetchRooms();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create room');
      return { success: false, error: err };
    }
  }, [fetchRooms]);

  /**
   * Update existing room
   */
  const updateRoom = useCallback(async (roomId, roomData) => {
    try {
      await apiService.put(`/admin/rooms/${roomId}`, roomData);
      toast.success('Room updated successfully');
      await fetchRooms();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update room');
      return { success: false, error: err };
    }
  }, [fetchRooms]);

  /**
   * Delete room
   */
  const deleteRoom = useCallback(async (roomId) => {
    try {
      await apiService.delete(`/admin/rooms/${roomId}`);
      toast.success('Room deleted successfully');
      await fetchRooms();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete room');
      return { success: false, error: err };
    }
  }, [fetchRooms]);

  /**
   * Filter rooms by type
   */
  const filterRoomsByType = useCallback((roomTypeId) => {
    if (roomTypeId === 'all') return rooms;
    return rooms.filter(room => room.room_type_id === roomTypeId);
  }, [rooms]);

  /**
   * Initial fetch
   * Now properly cleans up on unmount
   */
  useEffect(() => {
    const cleanup = fetchRooms();
    return () => {
      // Cancel ongoing request when component unmounts
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [fetchRooms]);

  return {
    rooms,
    loading,
    error,
    fetchRooms,
    createRoom,
    updateRoom,
    deleteRoom,
    filterRoomsByType,
  };
};
